import { useEffect, useRef, useState } from "react";
import type { StickVector } from "./control-types";

type KeyboardBinding = {
  positive: string[];
  negative: string[];
};

export type KeyboardVectorBindings = {
  horizontal: KeyboardBinding;
  vertical: KeyboardBinding;
};

type KeyState = Record<string, boolean>;

const DEFAULT_THRESHOLD = 1;

const clamp = (value: number) => Math.max(-1, Math.min(1, value));

export const useKeyboardVector = (
  bindings: KeyboardVectorBindings,
  options?: { enabled?: boolean }
): StickVector => {
  const enabled = options?.enabled ?? true;
  const [vector, setVector] = useState<StickVector>({ x: 0, y: 0 });
  const keyState = useRef<KeyState>({});

  useEffect(() => {
    if (!enabled) {
      setVector({ x: 0, y: 0 });
      return;
    }

    const handleKeyChange = (code: string, pressed: boolean) => {
      keyState.current[code] = pressed;
      
      //TODO: this caused a maximum depth exceeded once
      const computeAxis = ({ positive, negative }: KeyboardBinding) => {
        const pos = positive.some((key) => keyState.current[key] === true);
        const neg = negative.some((key) => keyState.current[key] === true);
        const raw = Number(pos) - Number(neg);
        if (raw === 0) return 0;
        return clamp(raw * DEFAULT_THRESHOLD);
      };

      setVector({
        x: computeAxis(bindings.horizontal),
        y: computeAxis(bindings.vertical),
      });
    };

    const onKeyDown = (event: KeyboardEvent) => {
      handleKeyChange(event.code, true);
    };

    const onKeyUp = (event: KeyboardEvent) => {
      handleKeyChange(event.code, false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      keyState.current = {};
      setVector({ x: 0, y: 0 });
    };
  }, [bindings.horizontal, bindings.vertical, enabled]);

  return vector;
};
